import React from 'react';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import IconButton from '@material-ui/core/IconButton';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useScrollTrigger from '@material-ui/core/useScrollTrigger';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import iconTMS from '../images/icon.jpg';
import { NavLink, Link as RouterLink } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
    appBar: {
        backgroundColor: 'rgba(255,255,255,0.75)',
        color: theme.palette.text.primary,
        boxShadow: 'none',
        transition: 'all .35s ease',
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
        backdropFilter: 'blur(12px)',
    },
    appBarElevated: {
        backgroundColor: 'rgba(250,250,252,0.95)',
        boxShadow: '0 12px 30px rgba(16, 55, 92, 0.15)',
    },
    toolbar: {
        minHeight: 64,
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    brand: {
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none',
    },
    brandLogo: {
        width: 52,
        height: 52,
        borderRadius: '50%',
        marginRight: theme.spacing(1.5),
        boxShadow: '0 10px 20px rgba(0,0,0,.2)',
    },
    brandName: {
        fontWeight: 700,
        letterSpacing: '-0.03em',
    },
    navLinks: {
        display: 'flex',
        alignItems: 'center',
    },
    navLink: {
        textDecoration: 'none',
        color: theme.palette.text.secondary,
        fontWeight: 600,
        padding: theme.spacing(0.75, 1.5),
        borderRadius: 999,
        margin: theme.spacing(0, 0.5),
        transition: 'all .2s ease',
        '&:hover': {
            color: theme.palette.primary.main,
            backgroundColor: 'rgba(16,55,92,0.08)',
        },
    },
    navLinkActive: {
        backgroundColor: theme.palette.primary.main,
        color: '#fff !important',
        boxShadow: '0 8px 20px rgba(16,55,92,0.25)',
    },
    mobileActions: {
        display: 'flex',
        alignItems: 'center',
    },
    menuButton: {
        marginLeft: theme.spacing(1),
    },
    drawerPaper: {
        width: '80vw',
        maxWidth: 320,
        padding: theme.spacing(2, 3, 4),
        background: theme.palette.background.default,
    },
    drawerHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    drawerBrand: {
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none',
        color: theme.palette.text.primary,
        fontWeight: 700,
    },
}));

const navItems = [
    { label: 'Beranda', path: '/' },
    { label: 'Produk', path: '/products/page/1' },
    { label: 'Berita', path: '/news/page/1' },
    { label: 'Tentang Kami', path: '/about' },
    { label: 'Hubungi Kami', path: '/contact' },
];

export default function Navigation(): JSX.Element {
    const classes = useStyles();
    const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 20 });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
    const [drawerOpen, setDrawerOpen] = React.useState(false);

    const toggleDrawer = (open: boolean) => () => {
        setDrawerOpen(open);
    };

    const closeDrawerAndNavigate = () => {
        setDrawerOpen(false);
    };

    return (
        <AppBar position="sticky" className={`${classes.appBar} ${trigger ? classes.appBarElevated : ''}`}>
            <Container maxWidth="xl">
                <Toolbar className={classes.toolbar}>
                    <RouterLink className={classes.brand} to="/home">
                        <img alt="" aria-hidden="true" src={iconTMS} className={classes.brandLogo} width="52" height="52" />
                        <Typography variant="h6" className={classes.brandName}>
                            PT. Tri Mustika Sejagat
                        </Typography>
                    </RouterLink>
                    {isMobile ? (
                        <div className={classes.mobileActions}>
                            <IconButton
                                color="inherit"
                                onClick={toggleDrawer(true)}
                                className={classes.menuButton}
                                aria-label="Toggle navigation menu"
                            >
                                <MenuIcon />
                            </IconButton>
                            <Drawer
                                anchor="right"
                                open={drawerOpen}
                                onClose={toggleDrawer(false)}
                                classes={{ paper: classes.drawerPaper }}
                            >
                                <div className={classes.drawerHeader}>
                                    <RouterLink to="/home" className={classes.drawerBrand} onClick={closeDrawerAndNavigate}>
                                        <img alt="" aria-hidden="true" src={iconTMS} className={classes.brandLogo} width="52" height="52" />
                                        <Typography variant="subtitle1">PT. Tri Mustika Sejagat</Typography>
                                    </RouterLink>
                                    <IconButton onClick={toggleDrawer(false)} aria-label="Tutup menu navigasi">
                                        <CloseIcon />
                                    </IconButton>
                                </div>
                                <Divider />
                                <List>
                                    {navItems.map((item) => (
                                        <ListItem
                                            key={item.path}
                                            button
                                            component={RouterLink}
                                            to={item.path}
                                            onClick={closeDrawerAndNavigate}
                                        >
                                            <ListItemText primary={item.label} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Drawer>
                        </div>
                    ) : (
                        <div className={classes.navLinks}>
                            {navItems.map((item) => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    exact={item.path === '/'}
                                    activeClassName={classes.navLinkActive}
                                    className={classes.navLink}
                                >
                                    {item.label.toUpperCase()}
                                </NavLink>
                            ))}
                        </div>
                    )}
                </Toolbar>
            </Container>
        </AppBar>
    );
}
